
/** @jsxImportSource @emotion/react */
import React,{ useCallback } from 'react';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import tw, { styled } from 'twin.macro';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useWorkshop from '../../hooks/useWorkshopMorale';
import useLocalization from '../../hooks/useLocalization';
import Products from '../../data/product.json';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import TotalRevenuePanel from '../TotalRevenuePanel';

const TIME_SCALE = 1;
const START_TIME = 17;

const WorkshopPanel = () => {

  const { currentWorkshop, setCurrentWorkshop, workshop, setWorkshop, currentSchedule, scheduleData } = useWorkshop();
  const { t, lang } = useLocalization();


  const renderTimeScale = useCallback(() => {
    let elem = [];
    let timePointer = START_TIME;
    do{
      elem.push(<TimeScale key={`timeScale_${timePointer.toString().padStart(2, "0")}:00`}>{`${timePointer.toString().padStart(2, "0")}:00`}</TimeScale>);
      timePointer += TIME_SCALE;
      if(timePointer === 24)
        timePointer = 0

    } while(timePointer !== START_TIME)

    return elem;
  },[]);
  
  return(
  <WorkshopPanelWrapper>
    <TotalRevenuePanel />
    <PanelHeader>
      {t(`workshop`)}: 
      <Tabs variant="fullWidth" value={currentWorkshop} onChange={(e, newValue) => {
        setCurrentWorkshop(newValue);
      }} centered>
        <WorkshopTab label={`1`} lang={lang} wrapped/>
        <WorkshopTab label={`2`} lang={lang} wrapped/>
        <WorkshopTab label={`3`} lang={lang} wrapped/>
        <WorkshopTab label={`4`} lang={lang} wrapped/>
      </Tabs>
      <WorkshopConfig>
        <TotalLabel>
        {t(`estimatedRevenue`)}：{scheduleData[currentWorkshop].total}
        </TotalLabel>
        <SliderHeader>
          <SliderLabel>{`${t(`workshopRank`)}：`}</SliderLabel>
          <SliderInput
              hiddenLabel
              value={workshop.workshops[currentWorkshop].rank}
              size="small"
              onChange={(e) => {
                setWorkshop({ key: 'rank', target : currentWorkshop, value : e.target.value });
              }}
            />
        </SliderHeader>
      </WorkshopConfig>
    </PanelHeader>
    <div css={tw`pt-[106px] h-full`}>
      <div css={tw`h-full overflow-auto`}>
        <TimeLineWrapper>
          {renderTimeScale()}
          <ScheduleWrapper>
            {currentSchedule.map((item,index) => 
                <ScheduleItem key={`schedule_${item}_${index}`} time={Products[item].crafttime} hasBonus={scheduleData[currentWorkshop].items[index].hasBonus}>
                  <div className={scheduleData[currentWorkshop].items[index].hasBonus ? `hasBonus` : ``}>
                    <ItemLabel><ItemImg src={Products[item].icon} />{Products[item].label[lang]}</ItemLabel>
                    {scheduleData[currentWorkshop].items[index].hasBonus && <Chip color="error" label={t(`hasBonus`)} size="small" css={tw`mt-2`}/>}
                    <PriceLabel>{t(`estimatedRevenue`)}：{scheduleData[currentWorkshop].items[index].price}</PriceLabel>
                    <Tooltip title={t(`removeFromSchedule`)} arrow placement="top">
                      <DeleteButton aria-label={t(`removeFromSchedule`)} size="small" onClick={() => {
                        setWorkshop({
                          key : 'removeSchedule',
                          target : currentWorkshop,
                          value : index
                        })
                      }}>
                        <DeleteIcon fontSize="inherit" />
                      </DeleteButton>
                    </Tooltip>
                  </div>
                </ScheduleItem>
              
            )}
          </ScheduleWrapper>
        </TimeLineWrapper>
        </div>
    </div>
  </WorkshopPanelWrapper>)
}

export default WorkshopPanel


const WorkshopPanelWrapper = styled.div`
  ${tw`
    bg-white
    translate-x-0
    z-20
    mr-5
    w-[314px]
    overflow-visible
    h-[calc(100vh - 130px)]
  `}

`

const PanelHeader = styled.div`
  ${tw`
    absolute
    top-0
    left-0
    w-full
    px-6
  `}
`

const TimeScale = styled.div`
  ${tw`
    text-black
    font-bold
    border-b
    border-b-gray-200
    border-dotted
  `}
`

const TimeLineWrapper = styled.div`
  ${tw`
    relative
    pb-8
    px-6
  `}
`

const ScheduleWrapper = styled.div`
  ${tw`
    absolute
    text-black
    top-0
    left-0
    pl-20
    pr-6
    w-full
  `}
`

const ScheduleItem = styled.div`
  ${tw`
  py-1
  flex
  w-full
  `}

  height: ${props => props.time*25}px;
  > div{
    ${tw`
      bg-white
      p-3
      pr-8
      rounded
      border-2
      text-[12px]
      w-full
      relative

    `}
    &.hasBonus{
      ${tw`
        border-red-500
        before:absolute
        before:w-0.5
        before:bg-red-500
        before:h-3
        before:-top-3
        before:left-[50%]
        before:ml-[-1px]
      `}
      -webkit-box-shadow: 0px 0px 10px 0px rgba(255,188,13,0.42); 
      box-shadow: 0px 0px 10px 0px rgba(255,188,13,0.42);
    }
  }
`

const TotalLabel = styled.div`
  ${tw`
    text-black
  `}
`

const WorkshopConfig = styled.div`
  ${tw`
    py-2
    px-0
    flex
    justify-between
  `}
`

const SliderHeader = styled.div`
  ${tw`
    flex
    items-center
    justify-between
  `}
`

const SliderLabel = styled(FormLabel)`
  ${tw`
  `}
`

const SliderInput = styled(TextField)`
  ${tw`
    w-7    
    inline-block
    
  `}
  input{
    ${tw`
      p-[2px]
      text-center
      text-[12px]
    `}
  }
`

const DeleteButton = styled(IconButton)`
  ${tw`
    absolute
    right-2
    top-2
  `}
`

const PriceLabel = styled.div`
  ${tw`
    absolute
    bottom-2.5
    right-2.5
  `}
`

const WorkshopTab = styled(Tab)`
  ${tw`
    px-1
    w-[25%]
    min-w-0
    text-[16px]
    
  `}


  ${(props) => props.lang === "en" ? tw`text-[12px]` : ``}

`

const ItemImg = styled.img`
  ${tw`
    w-5
    h-5
    absolute
    top-0
    left-0
  `}
`

const ItemLabel = styled.div`
  ${tw`
    relative
    pl-[28px]
  `}
`